/*
 * The UI side of the shared URL module (avoconsulte-be/src/seo/urls.js).
 * Builders return decoded paths: pass them to navigate() as-is and through
 * toHref() for href attributes.
 */
import urls from "@seo/urls";
import copy from "@seo/copy";

export { urls, copy };

// The language this document was booted in (boot.js: the URL's, else the
// saved choice). Kept in memory because localStorage is shared with every
// other tab and frame of the site: a language picked elsewhere must not
// leak into a page that is already showing one.
let pageLang = null;

/** Called by boot.js once it has decided the language. */
export function setPageLang(lang) {
  pageLang = urls.LANGS.includes(lang) ? lang : null;
}

/** The language the document was booted in, or null before boot (tests). */
export function bootLang() {
  return pageLang;
}

/** The page language, decided by boot.js from the URL. Read at render. */
export function currentLang() {
  if (pageLang) return pageLang;
  let lang = null;
  try {
    lang = localStorage.getItem("lang");
  } catch (e) {
    lang = null;
  }
  return urls.LANGS.includes(lang) ? lang : urls.DEFAULT_LANG;
}

/** The current pathname, decoded and without a trailing slash. */
export function currentPath(pathname = window.location.pathname) {
  let path = pathname;
  try {
    path = decodeURIComponent(pathname);
  } catch (e) {
    path = pathname;
  }
  return path.replace(/\/+$/, "") || "/";
}

/** Same path, ignoring a trailing slash and percent-encoding. */
export function samePath(a, b) {
  return currentPath(a) === currentPath(b);
}

/** href for a section listing (lawyers, questions, blogs, about, contact, terms). */
export function sectionHref(type, lang = currentLang()) {
  return urls.toHref(urls.sectionPath(type, lang));
}

/**
 * Where switching to `lang` should land, staying on the page being viewed:
 *   1. the page's own <link rel="alternate" hreflang> when it has one;
 *   2. the same address when it carries no language (dashboards, login,
 *      booking, a lawyer's short link): the saved choice decides there;
 *   3. the same page built for the other language (a lawyer, a city and
 *      level, a magazine topic, a section); questions and articles exist in
 *      one language only, so their section's list;
 *   4. the home page only when the address is not one of ours.
 * Returns an href (encoded, with the query string where it still applies).
 */
export function languageTarget(lang, { pathname, search = "", hash = "" }, alternateHref = null) {
  if (alternateHref) return alternateHref;
  const route = urls.parsePath(pathname, search);
  const keep = `${search || ""}${hash || ""}`;
  if (route.lang === null && route.type !== "unknown") return `${pathname}${keep}`;
  const { params = {} } = route;
  let path = null;
  switch (route.type) {
    case "home":
      path = urls.homePath(lang);
      break;
    case "lawyers":
      path = urls.lawyersPath(lang, { governorate: params.governorate, barLevel: params.barLevel, page: params.page });
      return urls.toHref(path);
    case "lawyer":
      // The slug is decorative: the server corrects it to the name in `lang`.
      path = `/${urls.SECTIONS.lawyer[lang]}/${params.slug}/${params.id}`;
      break;
    case "category":
      path = urls.categoryPath(params.category, lang);
      break;
    case "question":
      path = urls.sectionPath("questions", lang);
      break;
    case "blog":
      path = urls.sectionPath("blogs", lang);
      break;
    default:
      if (urls.SECTIONS[route.type]) path = urls.sectionPath(route.type, lang);
  }
  return urls.toHref(path || urls.homePath(lang));
}

/**
 * Switch language on the page being viewed (see languageTarget). A full
 * navigation, so the other direction's stylesheet loads.
 */
export function switchLanguage(lang) {
  try {
    localStorage.setItem("lang", lang);
  } catch (e) {
    // The target URL carries the language anyway.
  }
  const alternate = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
  window.location.assign(languageTarget(lang, window.location, alternate && alternate.href));
}
